import React, { useMemo } from "react";
import { Link } from "react-router-dom";

import bg from "../assets/background.png";
import Footer from "../components/footer";
import ContactForm from "../components/contactForm";
import Seo from "../components/Seo";
import { useSchedule, usePages } from "../data/useLocations";

const BRAND = "#d0142d";

const SR_ONLY = {
  position: "absolute",
  width: "1px",
  height: "1px",
  padding: 0,
  margin: "-1px",
  overflow: "hidden",
  clip: "rect(0,0,0,0)",
  whiteSpace: "nowrap",
  border: 0,
};

const sectionTitle = {
  color: "rgb(30,30,30)",
  fontSize: "20px",
  fontWeight: "bold",
  textAlign: "center",
  marginBottom: "-15px",
};

export default function Individual() {
  const { data, loading } = useSchedule();
  const { pages } = usePages();

  const individual = data?.individual || null;

  /** ID sali → podstrona, z której bierzemy nazwę, adres i kolor. */
  const pageByRoom = useMemo(() => {
    const map = new Map();
    pages.forEach((p) => {
      p.rooms.forEach((r) => map.set(r.id, { page: p, room: r }));
    });
    return map;
  }, [pages]);

  const types = individual?.types || [];

  /** Sale z przynajmniej jednym rodzajem zajęć w cenie. */
  const cards = useMemo(() => {
    if (!individual) return [];

    return (individual.locations || [])
      .map((loc) => {
        const match = pageByRoom.get(loc.locationId);
        if (!match) return null;

        const items = (loc.prices || [])
          .filter((p) => p.price != null)
          .map((p) => {
            const type = types.find((t) => t.id === p.typeId);
            if (!type) return null;
            return {
              id: p.typeId,
              name: type.name,
              price: p.price,
              unit: p.unit || null,
              note: p.note || null,
              order: type.order ?? 0,
            };
          })
          .filter(Boolean)
          .sort((a, b) => a.order - b.order);

        if (items.length === 0) return null;

        const { page, room } = match;

        return {
          id: loc.locationId,
          slug: page.slug,
          name: page.isMulti ? `${page.name} - ${room.label}` : page.name,
          address: page.address,
          color: page.color || BRAND,
          note: loc.note || null,
          items,
        };
      })
      .filter(Boolean);
  }, [individual, pageByRoom, types]);

  /** Dane dla formularza — bez przypisanej lokalizacji. */
  const leadLocation = useMemo(
    () => ({
      id: null,
      name: "Zajęcia indywidualne",
      slug: "zajecia-indywidualne",
      color: BRAND,
      area: null,
      interests: types.map((t) => t.name),
    }),
    [types]
  );

  return (
    <div
      style={{
        position: "relative",
        minHeight: "100vh",
        width: "100%",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "flex-start",
        gap: "40px",
        background: `#ededed url(${bg}) center / cover no-repeat`,
        paddingTop: "80px",
      }}
    >
      <Seo
        title="Zajęcia indywidualne"
        description="Treningi jeden na jeden z instruktorem - aerial, taniec i akrobatyka. Sprawdź, w których salach je organizujemy i ile kosztują."
        path="/zajecia-indywidualne"
      />

      <h1 style={SR_ONLY}>
        Zajęcia indywidualne 4STARS - treningi personalne z aerial, tańca i akrobatyki
      </h1>

      {/* HERO */}
      <section
        style={{
          width: "92%",
          maxWidth: "1000px",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: "14px",
          textAlign: "center",
          marginTop: "10px",
        }}
      >
        <h2
          style={{
            margin: 0,
            fontSize: "30px",
            fontWeight: 900,
            color: "rgb(30,30,30)",
            lineHeight: 1.25,
          }}
        >
          Zajęcia indywidualne
        </h2>

        <p
          style={{
            margin: 0,
            fontSize: "15px",
            fontWeight: 600,
            color: "rgba(0,0,0,0.65)",
            lineHeight: 1.6,
            maxWidth: "640px",
          }}
        >
          {individual?.lead ||
            "Trening jeden na jeden z instruktorem - w tempie dopasowanym do Ciebie, z pełną uwagą na technikę i bezpieczeństwo."}
        </p>
      </section>

      {/* OPIS */}
      {individual?.description ? (
        <section
          style={{
            width: "80%",
            maxWidth: "1000px",
            display: "flex",
            justifyContent: "center",
          }}
        >
          <p
            style={{
              margin: 0,
              color: "rgb(30,30,30)",
              fontSize: "15px",
              textAlign: "center",
              whiteSpace: "pre-line",
              lineHeight: 1.65,
            }}
          >
            {individual.description}
          </p>
        </section>
      ) : null}

      {/* STAN ŁADOWANIA */}
      {loading ? (
        <section style={{ width: "92%", maxWidth: "1200px", display: "flex", flexWrap: "wrap", gap: "14px", justifyContent: "center" }}>
          {[0, 1, 2].map((i) => (
            <span
              key={i}
              style={{
                flex: "1 1 320px",
                maxWidth: "380px",
                height: "220px",
                borderRadius: "22px",
                backgroundColor: "rgba(255,255,255,0.55)",
                border: "1px solid rgba(0,0,0,0.05)",
              }}
            />
          ))}
        </section>
      ) : null}

      {/* BRAK OFERTY */}
      {!loading && cards.length === 0 ? (
        <section
          style={{
            width: "92%",
            maxWidth: "700px",
            backgroundColor: "rgba(255,255,255,0.94)",
            border: "1px solid rgba(0,0,0,0.08)",
            borderRadius: "18px",
            padding: "28px 24px",
            textAlign: "center",
            display: "flex",
            flexDirection: "column",
            gap: "12px",
          }}
        >
          <h3 style={{ margin: 0, fontSize: "17px", fontWeight: 900, color: "rgba(0,0,0,0.9)" }}>
            Ofertę przygotowujemy
          </h3>
          <p style={{ margin: 0, fontSize: "14px", fontWeight: 600, color: "rgba(0,0,0,0.62)", lineHeight: 1.6 }}>
            Zadzwoń pod numer{" "}
            <a href="tel:+48505376552" style={{ color: BRAND, fontWeight: 900, textDecoration: "none" }}>
              505 376 552
            </a>{" "}
            albo napisz - ustalimy termin i cenę.
          </p>
        </section>
      ) : null}

      {/* CENNIK PER SALA */}
      {cards.length > 0 ? (
        <>
          <h2 style={sectionTitle}>Lokalizacje i cennik</h2>

          <section
            style={{
              width: "92%",
              maxWidth: "1200px",
              display: "flex",
              flexDirection: "row",
              flexWrap: "wrap",
              gap: "16px",
              justifyContent: "center",
            }}
          >
            {cards.map((c) => (
              <div
                key={c.id}
                style={{
                  flex: "1 1 320px",
                  minWidth: "300px",
                  maxWidth: "380px",
                  backgroundColor: "rgba(255,255,255,0.94)",
                  border: "1px solid rgba(0,0,0,0.08)",
                  borderTop: `8px solid ${c.color}`,
                  borderRadius: "22px",
                  boxShadow: "0 10px 24px rgba(0,0,0,0.06)",
                  display: "flex",
                  flexDirection: "column",
                  overflow: "hidden",
                }}
              >
                <div style={{ padding: "18px 20px 10px" }}>
                  <h3 style={{ margin: 0, fontSize: "16px", fontWeight: 900, color: "rgba(0,0,0,0.92)" }}>
                    {c.name}
                  </h3>
                  {c.address ? (
                    <p style={{ margin: "4px 0 0", fontSize: "12px", fontWeight: 700, color: "rgba(0,0,0,0.55)" }}>
                      {c.address}
                    </p>
                  ) : null}
                </div>

                <div style={{ display: "flex", flexDirection: "column", padding: "0 20px" }}>
                  {c.items.map((item, idx) => (
                    <div
                      key={item.id}
                      style={{
                        display: "flex",
                        flexDirection: "row",
                        alignItems: "flex-start",
                        justifyContent: "space-between",
                        gap: "12px",
                        padding: "12px 0 10px",
                        borderTop: idx === 0 ? "none" : "1px solid rgba(0,0,0,0.06)",
                      }}
                    >
                      <div style={{ display: "flex", flexDirection: "column", gap: "2px", minWidth: 0 }}>
                        <span style={{ fontSize: "13px", fontWeight: 800, color: "rgba(0,0,0,0.88)" }}>
                          {item.name}
                        </span>
                        {item.note ? (
                          <span style={{ fontSize: "11px", fontWeight: 600, color: "rgba(0,0,0,0.5)" }}>
                            {item.note}
                          </span>
                        ) : null}
                      </div>

                      <div style={{ textAlign: "right", flex: "0 0 auto" }}>
                        <div style={{ fontSize: "15px", fontWeight: 900, color: "rgba(0,0,0,0.92)" }}>
                          {item.price} zł
                        </div>
                        {item.unit ? (
                          <div style={{ fontSize: "10px", fontWeight: 700, color: "rgba(0,0,0,0.5)" }}>
                            {item.unit}
                          </div>
                        ) : null}
                      </div>
                    </div>
                  ))}
                </div>

                {c.note ? (
                  <p
                    style={{
                      margin: "8px 20px 0",
                      fontSize: "11px",
                      fontWeight: 700,
                      color: c.color,
                      lineHeight: 1.5,
                    }}
                  >
                    {c.note}
                  </p>
                ) : null}

                <Link
                  to={`/${c.slug}`}
                  style={{
                    marginTop: "auto",
                    textDecoration: "none",
                    textAlign: "center",
                    fontSize: "12px",
                    fontWeight: 900,
                    color: "rgb(250,250,250)",
                    backgroundColor: c.color,
                    padding: "12px",
                  }}
                >
                  Zobacz placówkę
                </Link>
              </div>
            ))}
          </section>
        </>
      ) : null}

      {/* SZCZEGÓŁY */}
      {(individual?.details || []).length > 0 ? (
        <>
          <hr style={{ border: `2px solid ${BRAND}`, width: "80%", borderRadius: "10px" }} />
          <h2 style={{ ...sectionTitle, marginTop: "-25px" }}>Dobrze wiedzieć</h2>
          <section
            style={{
              width: "92%",
              maxWidth: "1000px",
              display: "flex",
              flexDirection: "row",
              flexWrap: "wrap",
              gap: "14px",
              justifyContent: "center",
            }}
          >
            {individual.details.map((d, i) => (
              <div
                key={i}
                style={{
                  flex: "1 1 260px",
                  minWidth: "260px",
                  backgroundColor: "rgba(255,255,255,0.94)",
                  border: "1px solid rgba(0,0,0,0.08)",
                  borderRadius: "18px",
                  padding: "16px 18px",
                  boxShadow: "0 8px 24px rgba(0,0,0,0.06)",
                }}
              >
                <h3 style={{ margin: 0, fontSize: "13px", fontWeight: 900, color: BRAND }}>
                  {d.label}
                </h3>
                <p
                  style={{
                    margin: "6px 0 0",
                    fontSize: "13px",
                    fontWeight: 600,
                    color: "rgba(0,0,0,0.65)",
                    lineHeight: 1.55,
                    whiteSpace: "pre-line",
                  }}
                >
                  {d.text}
                </p>
              </div>
            ))}
          </section>
        </>
      ) : null}

      {/* FORMULARZ */}
      <hr style={{ border: `2px solid ${BRAND}`, width: "80%", borderRadius: "10px" }} />

      <h2 style={{ ...sectionTitle, marginTop: "-25px", marginBottom: "-45px" }} id="kontakt">
        Umów zajęcia indywidualne
      </h2>

      {individual?.contactNote ? (
        <p
          style={{
            margin: 0,
            marginTop: "35px",
            fontSize: "13px",
            fontWeight: 700,
            color: "rgba(0,0,0,0.6)",
            textAlign: "center",
            width: "90%",
            maxWidth: "700px",
          }}
        >
          {individual.contactNote}
        </p>
      ) : null}

      <section
        style={{
          display: "flex",
          flexDirection: "row",
          alignItems: "center",
          justifyContent: "flex-start",
          width: "90%",
          maxWidth: "1200px",
        }}
      >
        <ContactForm locationObj={leadLocation} />
      </section>

      <Footer />
    </div>
  );
}